import React, { useState, useEffect, useMemo } from 'react'
import { useChatContext } from 'stream-chat-react'
import type { Channel } from 'stream-chat'
import { useUserInfo } from 'hooks/useUserInfo'
import { useGetBusinessProfile, IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import { useGetAdvisorDashboardQuery } from '__generated__/api-types-and-hooks'
import { useTenantId } from 'utils/useTenantId'
import { USER_ROLES } from 'config'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { getInitials, getInitialsFromName } from 'components/Chat/helper'
import { formatMessageWithMentions } from 'components/Chat/helper/messageFormatter'
import { AppModalBox } from 'components/Common/AppModalBox/AppModalBox.component'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { getChipColorsFromText, getHexColorFromText } from 'appUtils/colorUtils'

interface ForwardMessageModalProps {
  show: boolean
  message: any
  onCancel: () => void
  onConfirm: (selectedUsers: string[], selectedChannels: Channel[]) => void
}

export const ForwardMessageModal: React.FC<ForwardMessageModalProps> = ({
  show,
  message,
  onCancel,
  onConfirm,
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
  const [selectedChannels, setSelectedChannels] = useState<Channel[]>([])
  const [users, setUsers] = useState<IAssigneeOptions[]>([])
  const [recentChats, setRecentChats] = useState<Channel[]>([])
  const [activeTab, setActiveTab] = useState<'recent' | 'contacts'>('recent')

  const { client } = useChatContext()
  const { userName } = useUserInfo()
  const tenantId = useTenantId()
  const loggedInUser = useSelector((state: RootState) => state.user.user)
  const isAdvisor = loggedInUser?.roles?.includes(USER_ROLES.BSO_ADVISOR)

  // Get business profile to access team members
  const { data: advisorData } = useGetAdvisorDashboardQuery({ tenantId })
  const { assigneeOptions: businessAssigneeOptions } = useGetBusinessProfile({ enabled: true })

  // Memoize assignee options
  const assigneeOptions = useMemo(() => {
    let options: IAssigneeOptions[] = []
    
    if (!isAdvisor) {
      options = businessAssigneeOptions || []
    } else {
      const response = JSON.parse(advisorData?.getAdvisorDashboard?.data || '{}')
      options =
        response?.accounts?.map((account: any) => ({
          label: `${account?.firstName} ${account?.lastName}`,
          value: account?.id,
          user: {
            id: account?.id,
            firstName: account?.firstName,
            lastName: account?.lastName,
          },
        })) || []
    }

    // Filter out current user
    return options.filter((option) => option.value !== loggedInUser?.id)
  }, [isAdvisor, businessAssigneeOptions, advisorData, loggedInUser?.id])

  useEffect(() => {
    if (show) {
      setUsers(assigneeOptions)
      
      // Get recent chats
      if (client) {
        const channels = Object.values(client.activeChannels)
          .filter((channel: any) => {
            // Filter out current channel and show only DM channels
            const members = Object.values(channel.state.members || {})
            return members.length === 2 // Only DM channels
          })
          .sort((a: any, b: any) => {
            // Sort by last message time
            const aTime = a.state.last_message_at || a.state.created_at
            const bTime = b.state.last_message_at || b.state.created_at
            return new Date(bTime).getTime() - new Date(aTime).getTime()
          })
          .slice(0, 10) // Get top 10 recent chats

        setRecentChats(channels as Channel[])
      }
      

    }
  }, [show, assigneeOptions, client])



  const filteredUsers = users.filter((user) =>
    user.label.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const filteredChats = recentChats.filter((channel: any) => {
    const members = Object.values(channel.state.members || {})
    const otherMember = members.find((member: any) => member?.user?.id !== client.userID) as any
    const userName = `${otherMember?.user?.firstName || ''} ${otherMember?.user?.lastName || ''}`.trim()
    return userName.toLowerCase().includes(searchQuery.toLowerCase())
  })

  const handleUserToggle = (userId: string) => {
    const newSelected = new Set(selectedUsers)
    if (newSelected.has(userId)) {
      newSelected.delete(userId)
    } else {
      newSelected.add(userId)
    }
    setSelectedUsers(newSelected)
  }

  const handleChannelSelect = (channel: Channel) => {
    setSelectedChannels(prev => 
      prev.find(ch => ch.cid === channel.cid)
        ? prev.filter(ch => ch.cid !== channel.cid)
        : [...prev, channel]
    )
  }

  const getChannelDisplayName = (channel: any) => {
    const members = Object.values(channel.state.members || {})
    const otherMember = members.find((member: any) => member?.user?.id !== client.userID) as any
    return `${otherMember?.user?.firstName || ''} ${otherMember?.user?.lastName || ''}`.trim() || 'Unknown User'
  }



  const handleForward = () => {
    onConfirm(Array.from(selectedUsers), selectedChannels)
    handleClose()
  }

  const handleClose = () => {
    setSearchQuery('')
    setSelectedUsers(new Set())
    setSelectedChannels([])
    setActiveTab('recent')
    onCancel()
  }

  if (!show) return null

  const hasSelections = selectedUsers.size > 0 || selectedChannels.length > 0
  const formattedText = message?.text ? formatMessageWithMentions(message.text) : ''

  return (
    <AppModalBox
      width="w-[500px]"
      backgroundColor="bg-white"
      childClass="p-0"
      showCancelButton={false}
      margin=""
      height="h-auto"
      isOnBoarding={false}
      padding="p-0"
      position="center"
      rounded="xl"
      showBorder={false}
    >
      <div className="flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-app-grey-30">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-app-black-100">Forward message</h3>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-app-grey-10 rounded-lg transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Search with Chips */}
        <div className="px-6 py-4">
          <div className="relative">
            <div className="flex items-center gap-1 flex-wrap min-h-[42px] px-3 py-1.5 bg-app-white-100 border border-app-grey-30 rounded-lg focus-within:ring-2 focus-within:ring-app-primary-60 focus-within:border-transparent">
              
              {/* Selected Users Chips */}
              {Array.from(selectedUsers).map((userId) => {
                const user = users.find(u => u.value === userId)
                if (!user) return null
                const userName = `${user.user?.firstName || ''} ${user.user?.lastName || ''}`.trim()
                const userInitials = getInitials({
                  firstName: user.user?.firstName || '',
                  lastName: user.user?.lastName || '',
                })
                const chipColors = getChipColorsFromText(userName)
                const avatarColor = getHexColorFromText(userName)
                
                return (
                  <div 
                    key={`user-${userId}`} 
                    className="inline-flex items-center gap-2 text-sm font-semibold"
                    style={{ 
                      backgroundColor: chipColors.backgroundColor, 
                      color: chipColors.textColor,
                      borderRadius: '8px',
                      paddingTop: '6px',
                      paddingRight: '12px',
                      paddingBottom: '6px',
                      paddingLeft: '12px'
                    }}
                  >
                    <div 
                      className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                      style={{ 
                        backgroundColor: avatarColor.color,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                        textShadow: avatarColor.isLight ? 'none' : '0px 1px 2px rgba(0, 0, 0, 0.2)',
                        boxSizing: 'border-box'
                      }}
                    >
                      {userInitials}
                    </div>
                    <span className="font-semibold" style={{ color: chipColors.textColor }}>{userName}</span>
                    <button
                      onClick={() => handleUserToggle(userId)}
                      className="w-4 h-4 opacity-70 hover:opacity-100 transition-opacity"
                      style={{ color: chipColors.textColor }}
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )
              })}
              
              {/* Selected Channels Chips */}
              {selectedChannels.map((channel) => {
                const displayName = getChannelDisplayName(channel)
                const members = Object.values(channel.state.members || {})
                const otherMember = members.find((member: any) => member?.user?.id !== client.userID) as any
                const channelInitials = getInitials({
                  firstName: otherMember?.user?.firstName || '',
                  lastName: otherMember?.user?.lastName || '',
                })
                const chipColors = getChipColorsFromText(displayName)
                const avatarColor = getHexColorFromText(displayName)
                
                return (
                  <div 
                    key={`channel-${channel.cid}`} 
                    className="inline-flex items-center gap-2 text-sm font-semibold"
                    style={{ 
                      backgroundColor: chipColors.backgroundColor, 
                      color: chipColors.textColor,
                      borderRadius: '8px',
                      paddingTop: '6px',
                      paddingRight: '12px',
                      paddingBottom: '6px',
                      paddingLeft: '12px'
                    }}
                  >
                    <div 
                      className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                      style={{ 
                        backgroundColor: avatarColor.color,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                        textShadow: avatarColor.isLight ? 'none' : '0px 1px 2px rgba(0, 0, 0, 0.2)',
                        boxSizing: 'border-box'
                      }}
                    >
                      {channelInitials}
                    </div>
                    <span className="font-semibold" style={{ color: chipColors.textColor }}>{displayName}</span>
                    <button
                      onClick={() => handleChannelSelect(channel)}
                      className="w-4 h-4 opacity-70 hover:opacity-100 transition-opacity"
                      style={{ color: chipColors.textColor }}
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )
              })}

              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 min-w-[100px] bg-transparent border-none outline-none text-sm placeholder-app-grey-55 px-1"
              />
              
              <svg
                className="w-4 h-4 text-app-grey-55 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-app-grey-30">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('recent')}
              className={`pb-3 border-b-2 font-medium text-sm transition-colors duration-150 ${
                activeTab === 'recent' 
                  ? 'border-app-primary-60 text-app-primary-60' 
                  : 'border-transparent text-app-grey-60 hover:text-app-grey-90'
              }`}
            >
              Recent chats
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`pb-3 border-b-2 font-medium text-sm transition-colors duration-150 ${
                activeTab === 'contacts' 
                  ? 'border-app-primary-60 text-app-primary-60' 
                  : 'border-transparent text-app-grey-60 hover:text-app-grey-90'
              }`}
            >
              All contacts
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto max-h-64">
          <div className="px-6">
            {activeTab === 'recent' ? (
              // Recent Chats Tab
              filteredChats.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-app-grey-60">No recent chats found</p>
                </div>
              ) : (
                filteredChats.map((channel) => {
                  const isSelected = selectedChannels.find(ch => ch.cid === channel.cid)
                  const displayName = getChannelDisplayName(channel)
                  const members = Object.values(channel.state.members || {})
                  const otherMember = members.find((member: any) => member?.user?.id !== client.userID) as any
                  
                  return (
                    <div
                      key={channel.cid}
                      className="flex items-center gap-3 py-3 cursor-pointer hover:bg-app-grey-10 rounded-lg px-2"
                      onClick={() => handleChannelSelect(channel)}
                    >
                      <AppAvatar
                        size="md"
                        type="mixed"
                        shape="Rounded"
                        text={getInitials({
                          firstName: otherMember?.user?.firstName || '',
                          lastName: otherMember?.user?.lastName || '',
                        })}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-app-black-100 truncate">{displayName}</p>
                      </div>
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected
                            ? 'bg-app-primary-60 border-app-primary-60'
                            : 'border-app-grey-40 bg-app-white-100 hover:border-app-primary-60'
                        }`}
                      >
                        {isSelected && (
                          <svg className="w-3 h-3 text-app-white-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                  )
                })
              )
            ) : (
              // All Contacts Tab
              filteredUsers.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-app-grey-60">
                    {searchQuery ? `No contacts found for "${searchQuery}"` : 'No contacts found'}
                  </p>
                </div>
              ) : (
                filteredUsers.map((user) => (
                  <div
                    key={user.value}
                    className="flex items-center gap-3 py-3 cursor-pointer hover:bg-app-grey-10 rounded-lg px-2"
                    onClick={() => handleUserToggle(user.value)}
                  >
                    <AppAvatar
                      size="md"
                      type="mixed"
                      shape="Rounded"
                      text={getInitials({
                        firstName: user.user?.firstName || '',
                        lastName: user.user?.lastName || '',
                      })}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-app-black-100 truncate">{user.label}</p>
                    </div>
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        selectedUsers.has(user.value)
                          ? 'bg-app-primary-60 border-app-primary-60'
                          : 'border-app-grey-40 bg-app-white-100 hover:border-app-primary-60'
                      }`}
                    >
                      {selectedUsers.has(user.value) && (
                        <svg className="w-3 h-3 text-app-white-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                ))
              )
            )}
          </div>
        </div>

        {/* Message Preview */}
        {formattedText && (
          <div className="px-6 py-4 border-t border-app-grey-30 bg-app-grey-5">
            <div className="flex items-end justify-end gap-3">
              <div className="bg-app-primary-60 rounded-2xl px-4 py-3 max-w-xs">
                <div className="text-sm text-app-white-100 leading-relaxed break-words">
                  {typeof formattedText === 'string' ? formattedText : message.text}
                </div>
              </div>
              <AppAvatar
                size="md"
                type="mixed"
                shape="Rounded"
                text={getInitialsFromName(userName)}
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-app-grey-30">
          <div className="flex items-center justify-between">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-app-grey-60 hover:text-app-black-100 transition-colors"
            >
              Copy link
            </button>
            <div className="flex gap-3">
              <AppButton
                size="md"
                variant={hasSelections ? "primary" : "secondary"}
                label="Forward"
                onClick={handleForward}
                disabled={!hasSelections}
              />
            </div>
          </div>
        </div>
      </div>
    </AppModalBox>
  )
}
